/**
 * Inline field-error rendering + notice banners. The server is the single
 * source of validation truth; the client adds no rule engine beyond file
 * type/size pre-checks. Every message shown here came from the REST response.
 */

import type { ErrorsMap, IewConfig } from './types';

export class ErrorRenderer {
  constructor(private readonly root: HTMLElement, private readonly config: IewConfig) {}

  clear(step?: number): void {
    const scope = step
      ? this.root.querySelector<HTMLElement>(`.iew-step[data-step="${step}"]`)
      : this.root;
    if (!scope) {
      return;
    }
    scope.querySelectorAll<HTMLElement>('.iew-field.has-error').forEach((field) => {
      field.classList.remove('has-error');
      const err = field.querySelector<HTMLElement>('.iew-field__error');
      if (err) {
        err.hidden = true;
        err.textContent = '';
        err.classList.remove('iew-field__error--notice');
      }
    });
  }

  /**
   * Render a field_key => messages map. `duplicate` styles the fiscal_code
   * error as a highlighted notice variant. Scrolls to and focuses the first
   * errored field.
   */
  render(errors: ErrorsMap, duplicate = false): void {
    let first: HTMLElement | null = null;

    Object.keys(errors).forEach((key) => {
      const field = this.root.querySelector<HTMLElement>(`.iew-field[data-field="${key}"]`);
      if (!field) {
        return;
      }
      const messages = errors[key] ?? [];
      const err = field.querySelector<HTMLElement>('.iew-field__error');
      field.classList.add('has-error');
      if (err) {
        err.textContent = messages.join(' ');
        err.hidden = false;
        if (duplicate && key === 'fiscal_code') {
          err.classList.add('iew-field__error--notice');
        }
      }
      if (!first) {
        first = field;
      }
    });

    if (first) {
      const target: HTMLElement = first;
      target.scrollIntoView({ behavior: 'smooth', block: 'center' });
      const control = target.querySelector<HTMLElement>('input, select, textarea');
      if (control) {
        control.focus({ preventScroll: true });
      }
    }
  }

  /**
   * A form-level notice (rate limit / spam / server / session errors), rendered
   * above the footer actions.
   */
  notice(message: string, kind: 'error' | 'info' = 'error', reloadLink = false): void {
    const form = this.root.querySelector<HTMLElement>('#iew-form') ?? this.root;
    let box = form.querySelector<HTMLElement>('.iew-notice');
    if (!box) {
      box = document.createElement('div');
      box.className = 'iew-notice';
      const footer = form.querySelector('.iew-footer');
      if (footer) {
        footer.parentElement?.insertBefore(box, footer);
      } else {
        form.appendChild(box);
      }
    }
    box.classList.toggle('iew-notice--info', kind === 'info');
    box.textContent = message;

    if (reloadLink) {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'iew-notice__reload';
      btn.textContent = this.config.i18n.reload ?? 'Reload';
      btn.addEventListener('click', () => window.location.reload());
      box.appendChild(document.createTextNode(' '));
      box.appendChild(btn);
    }

    box.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }
}
